import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import type { ParsedElement } from '@/pages/ActualDocumentPage';

interface ExportOptions {
  filename?: string;
  title?: string;
  subtitle?: string;
}

export async function exportToPdf(
  elements: ParsedElement[],
  options: ExportOptions = {}
): Promise<void> {
  const { filename = 'document.pdf', title = '', subtitle = '' } = options;

  const container = document.createElement('div');
  container.style.cssText = `
    position: fixed;
    left: -9999px;
    top: 0;
    width: 210mm;
    padding: 20mm;
    background: white;
    font-family: "Times New Roman", Times, serif;
    font-size: 14px;
    line-height: 1.6;
    color: #000;
  `;

  let html = buildExportHtml(elements, title, subtitle);
  container.innerHTML = html;
  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      logging: false,
      windowWidth: container.scrollWidth,
      windowHeight: container.scrollHeight,
    });

    // Extract base64 without data URL prefix
    const imgData = canvas.toDataURL('image/png');
    const base64Data = imgData.split(',')[1]; // Remove "data:image/png;base64," prefix

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const imgProps = pdf.getImageProperties(imgData);
    const imgWidth = imgProps.width;
    const imgHeight = imgProps.height;

    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
    const scaledWidth = imgWidth * ratio;
    const scaledHeight = imgHeight * ratio;

    let heightLeft = scaledHeight;
    let position = 0;

    // First page
    pdf.addImage(base64Data, 'PNG', 0, position, scaledWidth, scaledHeight);
    heightLeft -= pdfHeight;

    // Additional pages
    while (heightLeft > 0) {
      position = heightLeft - scaledHeight;
      pdf.addPage();
      pdf.addImage(base64Data, 'PNG', 0, position, scaledWidth, scaledHeight);
      heightLeft -= pdfHeight;
    }

    pdf.save(filename);
  } finally {
    document.body.removeChild(container);
  }
}

function buildExportHtml(
  elements: ParsedElement[],
  title: string,
  subtitle: string
): string {
  const parts: string[] = [];

  if (title) {
    parts.push(`<div style="text-align:center;font-size:18px;font-weight:bold;margin-bottom:8px;">${escapeHtml(title)}</div>`);
  }
  if (subtitle) {
    parts.push(`<div style="text-align:center;font-size:14px;color:#444;margin-bottom:24px;">${escapeHtml(subtitle)}</div>`);
  }

  for (const el of elements) {
    parts.push(renderElementForExport(el));
  }

  return parts.join('\n');
}

function renderElementForExport(el: ParsedElement): string {
  const content = el.content || '';

  switch (el.type) {
    case 'chapter':
      return `<div style="margin-top:32px;margin-bottom:16px;padding-top:16px;border-top:2px solid #333;text-align:center;font-weight:bold;font-size:16px;">
        Глава ${el.num || ''} ${content}
      </div>`;

    case 'article_header':
      return `<div style="margin-top:24px;margin-bottom:16px;text-align:center;font-weight:bold;font-size:15px;">
        Статья ${el.num || ''} ${content}
      </div>`;

    case 'doc_title':
      return `<div style="text-align:center;font-weight:bold;font-size:17px;margin:16px 0;">${content}</div>`;

    case 'law_name':
      return `<div style="text-align:center;font-weight:bold;font-size:16px;margin:12px 0;">${content}</div>`;

    case 'part_text':
      return `<div style="margin:8px 0;display:flex;align-items:baseline;">
        <span style="font-weight:bold;min-width:30px;">${el.num || ''}</span>
        <span>${content}</span>
      </div>`;

    case 'point':
      return `<div style="margin:4px 0 4px 20px;display:flex;align-items:baseline;">
        <span style="min-width:30px;">${el.num || ''}</span>
        <span>${content}</span>
      </div>`;

    case 'subpoint':
      return `<div style="margin:4px 0 4px 40px;display:flex;align-items:baseline;">
        <span style="min-width:25px;">${el.num || ''}</span>
        <span>${content}</span>
      </div>`;

    case 'paragraph':
      return `<p style="margin:8px 0;text-indent:1.5em;">${content}</p>`;

    case 'redaction_note':
      return `<div style="font-size:12px;color:#666;font-style:italic;margin:4px 0 4px 20px;padding:4px 8px;background:#f5f5f5;border-left:3px solid #ccc;">${content}</div>`;

    case 'application_note':
      return `<div style="font-size:13px;color:#444;margin:12px 0;padding:8px 12px;background:#fffbeb;border-left:3px solid #eab308;">${content}</div>`;

    default:
      return `<div>${content}</div>`;
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}